import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@material-ui/core";
import DataTable, { TableColumn } from "react-data-table-component";
import { MdAdd } from "react-icons/md";
import { toast } from "react-toastify";
import { useParams } from "react-router";

import { ContractorCompanyFormValues } from "../../../interface/formik/ContractorCompanyFormValues";
import { ContractorCompanyFormSchema } from "../schema/contractor-company-form-schema";
import contractorCompanyService from "../../../services/contractorCompanyService";
import { ContractorCompany } from "../../../interface/api/ContractorCompany";
import { history } from "../../..";
import { SetContractorCompanyAction } from "../../../interface/redux-types/SetContractorCompanyAction";
import {
  clearContractorCompanyContact,
  clearContractorProject,
  clearContractorProjects,
  getContractorCompany,
  getContractorCompanyContacts,
  getContractorProjects,
  setContractorCompany,
  setContractorCompanyContacts,
  setContractorProjects,
} from "../../../store/contractorStore";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import PageHero from "../../shared/page-hero";
import FormErrorMessage from "../../shared/form-error-message";
import FormTextField from "../../shared/form-text-field";
import { ContractorContact } from "../../../interface/api/ContractorContact";
import DeleteDialog from "../../shared/dialog/delete";
import {
  ActionIconGroup,
  EditIcon,
  DeleteIcon,
  PersonCheckFillIcon,
  PersonDashFillIcon,
  SoilFLOIcon,
  OverThresholdIcon,
  ProjectLogsIcon,
  SaltImpactedIcon,
  HaulageRecordReportIcon,
  ArchiveIcon,
  StatusIconGroup,
} from "../../shared/icons";
import Button from "../../shared/Button";
import { TabPanel } from "../../shared/tab-panel";
import { a11yProps } from "../../../utils/constants";
import PaginatedTableTop, {
  paginationTopClassName,
} from "../../shared/paginated-table-top";
import { tableCustomStyles } from "../../../utils/table-styles";
import contractorContactService from "../../../services/contractorContactService";
import { SetContractorContactsAction } from "../../../interface/redux-types/SetContractorContactsAction";
import { Project } from "../../../interface/api/Project";
import { SetContractorProjectsAction } from "../../../interface/redux-types/SetContractorProjectsAction";
import contractorProjectService from "../../../services/contractorProjectService";
import HaulageRecordReportDialog from "../../shared/dialog/haulage-record-report";
import { GenerateHaulageRecordReportFormValues } from "../../../interface/formik/GenerateHaulageRecordReportFormValues";

const useStyles = makeStyles({
  form: {
    marginTop: "25px",
  },
  sectionHeading: {
    fontWeight: 600,
    fontSize: "0.875rem",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    color: "#666",
    marginBottom: 4,
  },
  tableContainer: {
    marginTop: "2rem",
  },
  addIconButton: {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  },
});

const ContractorCompanyView = ({}) => {
  const classes = useStyles();
  const { id }: any = useParams();
  const dispatch = useAppDispatch();
  const contractorCompany = useAppSelector(getContractorCompany);
  const contractorContacts = useAppSelector(getContractorCompanyContacts);
  const contractorProjects = useAppSelector(getContractorProjects);

  const [tab, setTab] = useState(0);

  const [openContractorContactDialog, setOpenContractorContactDialog] =
    useState(false);

  const [openContractorProjectDialog, setOpenContractorProjectDialog] =
    useState(false);

  const [selectedContractorContactItem, setContractorContactSelectedItem] =
    useState<Partial<ContractorContact>>({});

  const [selectedContractorProjectItem, setContractorProjectSelectedItem] =
    useState<Partial<Project>>({});

  const [openHaulageRecordReportDialog, setOpenHaulageRecordReportDialog] =
    useState(false);

  const [selectedContractorProject, setSelectedContractorProject] = useState<
    Partial<Project>
  >({});

  const onOpenContractorContactDialog = () =>
    setOpenContractorContactDialog(true);

  const onCloseContractorContactDialog = () =>
    setOpenContractorContactDialog(false);

  const onOpenContractorProjectDialog = () =>
    setOpenContractorProjectDialog(true);

  const onCloseContractorProjectDialog = () =>
    setOpenContractorProjectDialog(false);

  const onCloseHaulageRecordReportDialog = () => {
    setSelectedContractorProject({});
    setOpenHaulageRecordReportDialog(false);
  };

  useEffect(() => {
    dispatch(clearContractorProject());
  }, []);

  useMemo(() => {
    if (!id || id === "new") return;

    contractorCompanyService.getContractorCompany(id).then((response) => {
      if (response.ok) {
        const contractorCompany = response.data as ContractorCompany;

        if (contractorCompany.isMiscellaneous) {
          toast.error("The Miscellaneous contractor cannot be edited.");
          history.push("/contractors");
          return;
        }

        const setContractorCompanyAction: SetContractorCompanyAction = {
          contractorCompany: contractorCompany,
        };

        dispatch(setContractorCompany(setContractorCompanyAction));
      } else {
        toast.error(response.originalError.message);
      }
    });

    contractorContactService
      .getContractorContacts({
        contractorCompanyId: id,
        contractorContact: null,
      })
      .then((response) => {
        if (response.ok) {
          const contractorContacts = response.data as ContractorContact[];
          const setContractorContactsAction: SetContractorContactsAction = {
            contractorContacts: contractorContacts,
          };
          dispatch(setContractorCompanyContacts(setContractorContactsAction));
        }
      });

    contractorProjectService.getContractorProjects(id).then((response) => {
      if (response.ok) {
        const projects = response.data as Project[];
        const setProjectsAction: SetContractorProjectsAction = {
          projects,
        };

        dispatch(setContractorProjects(setProjectsAction));
      } else {
        toast.error(response.originalError.message);
      }
    });
  }, []);

  const onSubmit = (
    values: ContractorCompanyFormValues,
    setSubmitting: Function,
  ) => {
    contractorCompanyService
      .saveContractorCompany(values as ContractorCompany)
      .then((response) => {
        if (response.ok) {
          history.push("/contractors");
        } else {
          const data = response.data as string;
          toast.error(data);
          setSubmitting(false);
        }
      });
  };

  const onConfirmContractorContactDelete = () => {
    if (selectedContractorContactItem && selectedContractorContactItem.id) {
      contractorContactService
        .deleteContractorContact(selectedContractorContactItem.id)
        .then((response) => {
          if (response.ok) {
            const setContractorContactsAction: SetContractorContactsAction = {
              contractorContacts: contractorContacts.filter(
                (c) => c.id !== selectedContractorContactItem.id,
              ),
            };
            dispatch(setContractorCompanyContacts(setContractorContactsAction));
          }
        });
    }

    onCloseContractorContactDialog();
  };

  const onConfirmContractorProjectDelete = () => {
    if (selectedContractorProjectItem && selectedContractorProjectItem.id) {
      contractorProjectService
        .deleteProject(selectedContractorProjectItem.id)
        .then((response) => {
          if (response.ok) {
            const setContractorProjectsAction: SetContractorProjectsAction = {
              projects: contractorProjects.filter(
                (c) => c.id !== selectedContractorProjectItem.id,
              ),
            };
            dispatch(setContractorProjects(setContractorProjectsAction));
          }
        });
    }

    onCloseContractorProjectDialog();
  };

  const onGenerateHaulageRecordReport = (
    values: GenerateHaulageRecordReportFormValues,
    setSubmitting: Function,
  ) => {
    if (!selectedContractorProject.id) {
      setSubmitting(false);
      return;
    }

    const payload = {
      from: values.from,
      to: values.to,
      fromDate: values.fromDate,
      toDate: values.toDate,
      emails: values.email ? [values.email] : [],
    };

    // Keep the dialog open with its in-progress status while the (potentially large)
    // report is generated server-side; close it once the email has been sent.
    contractorProjectService
      .emailHaulageRecordPdf(selectedContractorProject.id, payload)
      .then((response) => {
        if (response.ok) {
          toast.success("Haulage records emailed.");
          setOpenHaulageRecordReportDialog(false);
        } else {
          toast.error("Failed to generate haulage records.");
        }
      })
      .catch(() => toast.error("Failed to generate haulage records."))
      .finally(() => setSubmitting(false));
  };

  const initialValues: ContractorCompanyFormValues = contractorCompany
    ? contractorCompany
    : {
        id: 0,
        name: "",
        isMainTicket: true,
        isSmallQuantity: false,
        isHydrovac: false,
        isRubble: false,
        isMiscellaneous: false,
      };

  const projectColumns: TableColumn<Project>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Project ID",
      selector: (row) => row.projectId,
    },
    {
      name: "Job Site",
      selector: (row) => row.jobSite,
    },
    {
      name: "Site Contact",
      selector: (row) => row.siteContactName,
    },
    {
      name: "Office Contact",
      selector: (row) => row.officeContactName,
    },
    {
      name: "Sale Price",
      cell: (row) =>
        row.salePrice != null ? `$${row.salePrice.toFixed(2)}` : "",
    },
    {
      name: "Tickets Sold",
      selector: (row) => row.ticketsSold,
    },
    {
      name: "Approved",
      selector: (row) => row.approved,
    },
    {
      name: "Status",
      cell: (row) => (
        <StatusIconGroup>
          {row.saltImpacted && <SaltImpactedIcon />}
          {row.overThreshold && <OverThresholdIcon />}
          {row.isSoilFLOProject && <SoilFLOIcon />}
          {row.archived && <ArchiveIcon />}
        </StatusIconGroup>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionIconGroup spread>
          <HaulageRecordReportIcon
            onClick={() => {
              setSelectedContractorProject(row);
              setOpenHaulageRecordReportDialog(true);
            }}
          />
          <ProjectLogsIcon
            onClick={() => history.push(`/project-log/${row.id}`)}
          />
          <EditIcon
            onClick={() =>
              history.push(
                `/projects/${row.id}/contractor-company/${id}`,
              )
            }
          />
          <DeleteIcon
            onClick={() => {
              setContractorProjectSelectedItem(row);
              onOpenContractorProjectDialog();
            }}
          />
        </ActionIconGroup>
      ),
    },
  ];

  const contactColumns: TableColumn<ContractorContact>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
    },
    {
      name: "Number",
      selector: (row) => row.number,
    },
    {
      name: "Email",
      selector: (row) => row.email,
    },
    {
      name: "Office Contact",
      cell: (row) =>
        row.isOfficeContact ? <PersonCheckFillIcon /> : <PersonDashFillIcon />,
    },
    {
      name: "Site Contact",
      cell: (row) =>
        row.isSiteContact ? <PersonCheckFillIcon /> : <PersonDashFillIcon />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionIconGroup spread>
          <EditIcon
            onClick={() =>
              history.push(
                `/contacts/${row.id}/contractor-company/${id}`,
              )
            }
          />
          <DeleteIcon
            onClick={() => {
              setContractorContactSelectedItem(row);
              onOpenContractorContactDialog();
            }}
          />
        </ActionIconGroup>
      ),
    },
  ];

  return (
    <Container maxWidth="xl">
      <PageHero
        title={
          id > 0
            ? `${contractorCompany ? contractorCompany.name : id}`
            : "New Contractor Company"
        }
        subtitle={
          id > 0 ? "Edit Contractor Company." : "Add Contractor Company."
        }
        showBackButton={true}
      />
      <div>
        <Formik
          initialValues={initialValues}
          validationSchema={ContractorCompanyFormSchema}
          onSubmit={(values, { setSubmitting }) =>
            onSubmit(values, setSubmitting)
          }
          enableReinitialize={true}
        >
          {({ values, handleSubmit, isSubmitting, setFieldValue }) => (
            <form onSubmit={handleSubmit} className={classes.form}>
              <Grid container spacing={3}>
                {/* ── Name + Save ── */}
                <Grid item xs={12} md={6}>
                  <FormTextField
                    name={"name"}
                    label={"Name"}
                    value={values.name}
                  />
                  <FormErrorMessage name="name" />
                  <Box mt={2}>
                    <Button
                      label="Save"
                      color="primary"
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                    />
                  </Box>
                </Grid>

                {/* ── Workstreams ── */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" style={{ padding: 16 }}>
                    <Typography className={classes.sectionHeading}>
                      Workstreams
                    </Typography>
                    <Grid container>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={values.isMainTicket}
                              onChange={() =>
                                setFieldValue(
                                  "isMainTicket",
                                  !values.isMainTicket,
                                )
                              }
                            />
                          }
                          label="Main Ticket"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={values.isSmallQuantity}
                              onChange={() =>
                                setFieldValue(
                                  "isSmallQuantity",
                                  !values.isSmallQuantity,
                                )
                              }
                            />
                          }
                          label="Small Quantity"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={values.isHydrovac}
                              onChange={() =>
                                setFieldValue("isHydrovac", !values.isHydrovac)
                              }
                            />
                          }
                          label="Hydrovac"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={values.isRubble ?? false}
                              onChange={() =>
                                setFieldValue("isRubble", !values.isRubble)
                              }
                            />
                          }
                          label="Rubble"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
        <div className={classes.tableContainer}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Projects" {...a11yProps(0)} />
              <Tab label="Contacts" {...a11yProps(1)} />
            </Tabs>
            <Tooltip title={tab === 0 ? "Add Project" : "Add Contact"}>
              <IconButton
                color="primary"
                className={classes.addIconButton}
                onClick={() => {
                  if (tab === 0) {
                    dispatch(clearContractorProjects());
                    history.push(`/projects/new/contractor-company/${id}`);
                  } else {
                    dispatch(clearContractorCompanyContact());
                    history.push(`/contacts/new/contractor-company/${id}`);
                  }
                }}
              >
                <MdAdd size={24} />
              </IconButton>
            </Tooltip>
          </Box>

          <TabPanel value={tab} index={0}>
            <Box mt={2}>
              <PaginatedTableTop>
                <DataTable
                  className={paginationTopClassName}
                  columns={projectColumns}
                  data={contractorProjects ?? []}
                  customStyles={tableCustomStyles}
                  striped
                  highlightOnHover
                  responsive
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[25, 50, 100, 200]}
                />
              </PaginatedTableTop>
            </Box>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Box mt={2}>
              <PaginatedTableTop>
                <DataTable
                  className={paginationTopClassName}
                  columns={contactColumns}
                  data={contractorContacts ?? []}
                  customStyles={tableCustomStyles}
                  striped
                  highlightOnHover
                  responsive
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[25, 50, 100, 200]}
                />
              </PaginatedTableTop>
            </Box>
          </TabPanel>

          <DeleteDialog
            open={openContractorProjectDialog}
            onClose={onCloseContractorProjectDialog}
            onConfirmDelete={onConfirmContractorProjectDelete}
          />
          <DeleteDialog
            open={openContractorContactDialog}
            onClose={onCloseContractorContactDialog}
            onConfirmDelete={onConfirmContractorContactDelete}
          />
          <HaulageRecordReportDialog
            open={openHaulageRecordReportDialog}
            onClose={onCloseHaulageRecordReportDialog}
            onSubmit={onGenerateHaulageRecordReport}
          />
        </div>
      </div>
    </Container>
  );
};

export default connect()(ContractorCompanyView);
